<script setup lang="ts">
import { ref } from 'vue';
import EmbedHtml from '~/components/Common/EmbedHtml.vue';
import Container from '~/components/Container.vue';
import { Icon } from '@iconify/vue';

const selectedElement = ref<number | string | null | symbol>(null);

function selectElement(index: number | null | string | symbol) {
  if (selectedElement.value === index) {
    selectedElement.value = null;
  } else {
    selectedElement.value = index;
  }
}

interface Props {
  anchor?: string;
  title?: string;
  description?: string;
  items: {
    title: string;
    content: string;
  }[];
}

defineProps<Props>();
</script>

<template>
  <section v-if="items && items.length > 0" :id="anchor" class="accordion">
    <Container is-fluid class="accordion__wrapper">
      <div class="accordion__box">
        <Container class="accordion__container">
          <div v-if="title || description" class="accordion__heading">
            <h2 v-if="title" class="accordion__title">{{ title }}</h2>
            <p v-if="description" class="accordion__description">{{ description }}</p>
          </div>
          <ul class="accordion__list">
            <li v-for="(item, index) in items" :key="`accordion-item-${index}`" class="accordion__item">
              <button
                type="button"
                class="group accordion__toggle"
                :class="{ active: selectedElement === index }"
                @click.prevent="selectElement(index)"
              >
                <strong class="accordion__toggle-title">{{ item.title }}</strong>
                <Icon icon="carbon:chevron-down" class="accordion__toggle-icon" />
              </button>
              <EmbedHtml :content="item.content" class="hidden" />
            </li>
          </ul>
        </Container>
      </div>
    </Container>
  </section>
</template>

<style lang="scss" scoped>
.accordion {
  @apply my-10 overflow-hidden;
  @apply md:my-16;
  @apply xl:my-24;

  &__box {
    @apply rounded-10 bg-light-accent;
    @apply xl:rounded-30;
  }

  &__container {
    @apply py-12;
    @apply xl:py-24;
  }

  &__heading {
    @apply flex flex-col items-center text-center mb-6 gap-6;
    @apply xl:mb-12 xl:gap-10;
  }

  &__title {
    @apply font-semibold text-[24px] leading-[1.1];
    @apply xl:text-[38px];
  }

  &__description {
    @apply font-medium leading-[1.3] text-[16px] text-base-color/80;
    @apply xl:text-[20px] xl:max-w-[60%] xl:mx-auto;
  }

  &__list {
    @apply flex flex-col gap-y-6;
    @apply xl:max-w-[1000px] xl:mx-auto;
  }

  &__item {
    @apply w-full rounded-10 bg-white;
  }

  &__toggle {
    @apply px-4 py-6 flex justify-between items-center w-full text-base-color;
    @apply xl:px-8 xl:py-10;

    &-title {
      @apply font-bold mr-12 text-[18px] leading-[1.1] text-start w-[85%] duration-300;
      @apply 2xl:text-[24px];
    }

    &-icon {
      @apply w-8 h-8 text-base-color/50 duration-300;
    }

    &.active {
      @apply text-accent;

      & + .text-container {
        @apply max-h-[99999px] px-4 pb-6 block;
        @apply xl:px-8 xl:pb-10;
      }

      svg {
        @apply rotate-180 text-accent;
      }
    }
  }

  &__toggle:hover &__toggle-title,
  &__toggle:hover &__toggle-icon {
    @apply text-accent;
  }

  .text-container {
    @apply max-h-0 overflow-hidden;

    & > * {
      @apply mb-4 last:mb-0;
      @apply xl:mb-8;
    }
  }
}
</style>
